import React, { FC, WheelEventHandler, memo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormChildQuestionDto, FormQuestionDto } from "@green0meter/api-client";

import CurrencyAdornment from "./components/CurrencyAdornment";

import { FormControl, InputAdornment, TextField } from "@components/base";
import { useDraftReturn } from "@hooks/useDraftField";

interface NumberInput {
  parentQuestionCode?: string;
  question: FormQuestionDto | FormChildQuestionDto;
  disabled?: boolean;
  disableDecimal?: boolean;
  draftField?: useDraftReturn | null;
}

const NumberInput: FC<NumberInput> = ({
  parentQuestionCode,
  question,
  disabled,
  disableDecimal,
  draftField,
}) => {
  const { control } = useFormContext();

  const disableWheel: WheelEventHandler<HTMLDivElement> = (e) =>
    e.target instanceof HTMLElement && e.target.blur();

  return (
    <FormControl fullWidth disabled={disabled}>
      <Controller
        name={
          parentQuestionCode
            ? `${parentQuestionCode}-${question.code}`
            : question.code
        }
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            onWheel={disableWheel}
            variant="filled"
            type="number"
            placeholder={(question as FormQuestionDto).placeholder || question.name}
            value={field.value ?? ""}
            onFocus={draftField?.handleFocus}
            onBlur={draftField?.handleBlur}
            onChange={(event) => {
              // Disable negative values
              if (parseInt(event.target.value) < 0) {
                event.target.value = `${event.target.value.replace("-", "")}`;
              }

              // Disable decimal values if disableDecimal is true
              if (disableDecimal) {
                event.target.value = event.target.value.replace(/[^0-9]/g, "");
              }

              field.onChange(event);
            }}
            slotProps={{
              input: {
                disabled,
                endAdornment: question.isCurrencyInput ? (
                  <CurrencyAdornment />
                ) : question.unit ? (
                  <InputAdornment position="end">
                    {question.unit.abbreviation}
                  </InputAdornment>
                ) : null,
                inputProps: {
                  min: 0,
                },
              },
            }}
          />
        )}
      />
    </FormControl>
  );
};

export default memo(NumberInput);
